import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { X, Loader2, Sparkles, Trophy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { CoinIcon } from "@/components/CoinBadge";

export default function ScratchCard({ open, onClose, onPointsChange }) {
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [limit, setLimit] = useState(1);
  const [reward, setReward] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const started = useRef(false);
  const inflight = useRef(false);
  const played = remaining <= 0;

  useEffect(() => {
    if (!open) return;
    window.history.pushState({ zpGame: true }, "");
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [open]);
  const close = () => { if (window.history.state?.zpGame) window.history.back(); else onClose(); };

  useEffect(() => {
    if (!open) return;
    setLoading(true); setRevealed(false); setReward(null); started.current = false; inflight.current = false;
    api.get("/games/scratch-card").then(({ data }) => {
      setPoints(data.points || 0);
      setLimit(data.limit ?? 1); setRemaining(data.remaining ?? 0);
      if ((data.remaining ?? 0) <= 0 && data.result) { setReward(data.result.coins); setRevealed(true); }
    }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open]);

  const paintFoil = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, c.width, c.height);
    g.addColorStop(0, "#D4AF37"); g.addColorStop(0.5, "#F5D97B"); g.addColorStop(1, "#C79A2E");
    ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, c.height);
    ctx.fillStyle = "rgba(11,19,43,0.55)"; ctx.font = "bold 20px sans-serif"; ctx.textAlign = "center";
    ctx.fillText("SCRATCH HERE", c.width / 2, c.height / 2 + 6);
  };

  // (re)paint foil whenever a fresh, unrevealed card is available
  useEffect(() => {
    if (loading || revealed || played || !open) return;
    const t = setTimeout(paintFoil, 60);
    return () => clearTimeout(t);
  }, [loading, revealed, played, open]);

  const beginPlay = async () => {
    if (started.current || inflight.current) return;
    inflight.current = true; started.current = true;
    try {
      const { data } = await api.post("/games/scratch-card/scratch");
      setReward(data.coins);
      setRemaining(data.remaining ?? 0);
      setPoints(data.balance); onPointsChange && onPointsChange(data.balance);
    } catch (e) { toast.error(apiError(e)); started.current = false; }
    finally { inflight.current = false; }
  };

  const newCard = () => {
    setRevealed(false); setReward(null); started.current = false; inflight.current = false;
  };

  const scratchAt = (x, y) => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath(); ctx.arc(x, y, 22, 0, Math.PI * 2); ctx.fill();
    ctx.globalCompositeOperation = "source-over";
  };

  const checkReveal = () => {
    const c = canvasRef.current; if (!c || revealed) return;
    const ctx = c.getContext("2d");
    const img = ctx.getImageData(0, 0, c.width, c.height).data;
    let clear = 0; for (let i = 3; i < img.length; i += 40) if (img[i] === 0) clear++;
    if (clear / (img.length / 40) > 0.5) {
      setRevealed(true);
      ctx.clearRect(0, 0, c.width, c.height);
      if (reward > 0) confetti({ particleCount: 130, spread: 75, origin: { y: 0.45 }, colors: ["#14B8A6", "#F5B301", "#ffffff"] });
    }
  };

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return [(t.clientX - r.left) * (c.width / r.width), (t.clientY - r.top) * (c.height / r.height)];
  };
  const down = (e) => { drawing.current = true; beginPlay(); const [x, y] = pos(e); scratchAt(x, y); };
  const move = (e) => { if (!drawing.current) return; const [x, y] = pos(e); scratchAt(x, y); };
  const up = () => { if (!drawing.current) return; drawing.current = false; checkReveal(); };

  if (!open) return null;
  const canScratch = !revealed && remaining > 0;
  const remainingLabel = played && !remaining ? "No cards left today • come back tomorrow" : `${remaining} of ${limit} card${limit > 1 ? "s" : ""} left today`;
  return createPortal((
    <div className="fixed inset-0 z-[100] flex flex-col bg-[#0B132B]" data-testid="scratch-modal">
      <header className="relative flex items-center justify-between px-4 py-4 sm:px-8">
        <button onClick={close} data-testid="scratch-back-btn" className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"><X size={20} /></button>
        <div className="flex items-center gap-2 font-display text-lg font-bold text-white"><Sparkles size={18} className="text-[#F5B301]" /> Scratch Card</div>
        <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/10 px-3 py-1.5 text-white"><CoinIcon size={18} /><span data-testid="scratch-points" className="font-display text-base font-bold">{points.toLocaleString("en-IN")}</span></div>
      </header>
      <div className="relative flex flex-1 flex-col items-center justify-center gap-6 px-4 pb-10">
        {loading ? <Loader2 className="h-8 w-8 animate-spin text-[#14B8A6]" /> : (
          <>
            <p className="max-w-sm text-center text-sm text-white/70">Scratch the golden foil to reveal your ZRo Pe Coins. The reward is decided securely on our server.</p>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/90" data-testid="scratch-remaining">{remainingLabel}</span>
            <div className="relative h-52 w-80 max-w-[86vw] overflow-hidden rounded-2xl border-4 border-[#F5B301] bg-gradient-to-br from-[#101a38] to-[#14224a] shadow-2xl">
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white" data-testid="scratch-reward">
                {reward === 0 ? (
                  <>
                    <p className="font-display text-2xl font-extrabold text-white">BETTER LUCK</p>
                    <p className="font-display text-2xl font-extrabold text-white">NEXT TIME</p>
                  </>
                ) : (
                  <>
                    <CoinIcon size={54} />
                    <p className="font-display text-3xl font-extrabold text-[#F5B301]">{reward === null ? "?" : reward}</p>
                    <p className="text-sm font-semibold">ZRo Pe Coins</p>
                  </>
                )}
              </div>
              {canScratch && (
                <canvas ref={canvasRef} width={320} height={208}
                  className="absolute inset-0 h-full w-full cursor-crosshair touch-none"
                  data-testid="scratch-canvas"
                  onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
                  onTouchStart={down} onTouchMove={move} onTouchEnd={up} />
              )}
            </div>
            <div className="min-h-[64px] text-center">
              {revealed ? (
                reward > 0 ? (
                <div className="animate-in fade-in zoom-in rounded-2xl border border-[#F5B301]/40 bg-[#F5B301]/10 px-6 py-4 text-white" data-testid="scratch-win">
                  <p className="flex items-center justify-center gap-2 font-display text-xl font-extrabold text-[#F5B301]"><Trophy size={20} /> Congratulations!</p>
                  <p className="mt-1 flex items-center justify-center gap-1.5 font-display text-2xl font-black text-white">+{reward} <CoinIcon size={24} /></p>
                  <p className="text-xs text-white/70">ZRo Pe Coins added to your balance</p>
                  <p className="mt-1 text-[11px] text-white/60">{remainingLabel}</p>
                  {remaining > 0 && (
                    <button onClick={newCard} data-testid="scratch-newcard" className="mt-3 rounded-lg bg-gradient-to-br from-[#14B8A6] to-[#0D9488] px-5 py-2 text-sm font-bold text-white transition active:scale-95">New card</button>
                  )}
                </div>
                ) : (
                <div className="animate-in fade-in zoom-in rounded-2xl border border-white/15 bg-white/10 px-6 py-4 text-white" data-testid="scratch-loss">
                  <p className="font-display text-xl font-extrabold text-white">Better luck next time!</p>
                  <p className="mt-1 text-xs text-white/70">No coins this time — try again soon.</p>
                  <p className="mt-1 text-[11px] text-white/60">{remainingLabel}</p>
                  {remaining > 0 && (
                    <button onClick={newCard} data-testid="scratch-newcard" className="mt-3 rounded-lg bg-gradient-to-br from-[#14B8A6] to-[#0D9488] px-5 py-2 text-sm font-bold text-white transition active:scale-95">New card</button>
                  )}
                </div>
                )
              ) : played ? (
                <p className="text-sm text-white/70">No cards left today. Come back tomorrow!</p>
              ) : (
                <p className="text-sm text-white/50">Drag across the card to scratch.</p>
              )}
            </div>
            <button onClick={close} data-testid="scratch-back-games" className="rounded-full bg-white/10 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20">Back to Games</button>
          </>
        )}
      </div>
    </div>
  ), document.body);
}
